import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Mail, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import Footer from "../components/Footer";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setVerifying(false);
      setError("Invalid verification link.");
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await fetch(`${API}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      // Clone response before reading to avoid "body stream already read" error
      const responseClone = response.clone();
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, try to get text
        const text = await responseClone.text();
        throw new Error(text || 'Verification failed');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }

      setSuccess(true);
    } catch (error) {
      setError(error.message || 'Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md animate-fade-in">
            <Link to="/" className="block text-center mb-12">
              <h1 
                className="text-3xl font-light text-[#2D2A26]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                the soft ritual
              </h1>
            </Link>

            <div className="paper-card rounded-3xl p-8 md:p-10 text-center">
              <Loader2 className="w-12 h-12 text-[#A87C6D] mx-auto mb-6 animate-spin" />
              <h2 
                className="text-2xl font-normal text-[#2D2A26] mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Verifying your email...
              </h2>
              <p className="text-[#6E6A66] font-light">
                Just a moment while we confirm your email address.
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md animate-fade-in">
            <Link to="/" className="block text-center mb-12">
              <h1 
                className="text-3xl font-light text-[#2D2A26]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                the soft ritual
              </h1>
            </Link>

            <div className="paper-card rounded-3xl p-8 md:p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-[#C8553D]/10 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-[#C8553D]" />
              </div>
              <h2 
                className="text-2xl font-normal text-[#2D2A26] mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Verification failed
              </h2>
              <p className="text-[#6E6A66] font-light mb-6">
                {error}
              </p>
              <div className="flex flex-col gap-3">
                <Link to="/login">
                  <Button className="w-full rounded-full bg-[#A87C6D] hover:bg-[#956d5f] text-white">
                    Sign in to resend
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="ghost" className="w-full rounded-full text-[#6E6A66]">
                    Back to home
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md animate-fade-in">
          <Link to="/" className="block text-center mb-12">
            <h1 
              className="text-3xl font-light text-[#2D2A26]"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              the soft ritual
            </h1>
          </Link>

          <div className="paper-card rounded-3xl p-8 md:p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-[#7A9E7E]/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#7A9E7E]" />
            </div>
            <h2 
              className="text-2xl font-normal text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Email verified
            </h2>
            <p className="text-[#6E6A66] font-light mb-6">
              Thank you for verifying your email. Your account is now fully activated.
            </p>
            <Link to="/dashboard">
              <Button className="rounded-full px-8 bg-[#A87C6D] hover:bg-[#956d5f] text-white">
                Go to your space
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
